import { constants as config } from '@config';
import tableQuery from 'assets/js/editor/table/table-query';
import { calculateColumnsWidth } from 'assets/js/helpers/columnsHelper';
import * as TextStylingService from 'src/services/text-styling';
import { convertRemToPixel } from 'src/utils/toolbelt';

export function loadTextEvents (popup, popupMenuCommonFunctions, canvas) {
  let tempMargin = 0;
  let range, activeParagraphIndex, textarea, numberOfParagraphs, columns;
  let firstLineMargin;
  let textAnchorFactor;
  let tablePaddingLeft = 0;
  let tablePaddingRight = 0;
  let isTableCell = false;

  const shortTimeOut = 60;
  const maxLineMargin = 200;

  const isNativeBehavior = INKIVE.C.IS_MOBILE_APP && (
    (!window.Android && INKIVE.Helpers.mobileVersion.isGreaterThan('1.1.1')) ||
    (window.Android && !INKIVE.Helpers.mobileVersion.isLowerThan('1.1.3'))
  );

  isNativeBehavior && popup.button('on_fontfamily').on('click', function () {
    const $fontFamily = $(popup.button('on_fontfamily'));
    const element = $(popup.element()).is('text')
      ? popup.element()
      : $(popup.element()).find('text')[0];

    const data = {
      font_family: element.getAttribute('font-family')
    };

    if (element.getAttribute('font-weight') === 'bold') {
      data.font_weight = element.getAttribute('font-weight');
    }

    if (element.getAttribute('font-style') === 'italic') {
      data.font_style = element.getAttribute('font-style');
    }

    if (element.getAttribute('text-decoration')) {
      data.text_decoration = element.getAttribute('text-decoration');
    }

    let text = '';

    $(element).children().each(function () {
      text += $(this).text();
      text += $(this).hasClass('eol')
        ? '\n'
        : ' ';
    });

    AppBridge.callBoth('pick_font', _.extend(data, {
      text,
      font_size: parseFloat($(popup.button('on_fontsize')).find('input').val()),
      position: _.extend($fontFamily.offset(), {
        height: $fontFamily.height(),
        width: $fontFamily.width()
      })
    }));

    AppBridge.off('ik.editor.text_update');
    AppBridge.on('ik.editor.text_update', function (event, data) {
      data = {
        font: {
          family: data.font_family,
          size: data.font_size
        }
      };

      AppBridge.design.set(data);
      INKIVE.Tool._cache.favFonts = null;
      popupMenuCommonFunctions.updateFontRelatedMenu(popup);

      data.font.size && popup.button('on_fontsize').find('input').val(
        parseInt(data.font.size)
      );
    });

    $fontFamily.removeClass('active')
      .find('.ui-dropdown-items')
      .removeAttr('style');
  });

  function getSelectedTextFromTable (tableElement) {
    const rects = [...tableElement.querySelectorAll('rect.table-cell-selected')];
    if (rects.length) {
      return rects.map(rect => rect.parentElement);
    }
    return tableElement.querySelectorAll('g.textarea');
  }

  function getSelectedTextAreas (selectedElements) {
    const textAreaElements = [];
    for (const selectedElement of selectedElements) {
      if (INKIVE.DesignFacade.getType(selectedElement) === 'text') {
        textAreaElements.push(selectedElement);
      } else if (INKIVE.DesignFacade.getType(selectedElement) === 'table') {
        const selectedTextElements = getSelectedTextFromTable(selectedElement);
        textAreaElements.push(...selectedTextElements);
      }
    }

    return textAreaElements;
  }

  popup.button('on_fontfamily').on('change', function (e, val, fontFamily, fontWeight, heading, fontStretch, fontStyle) {
    if ($(e.target).is('input')) return;

    INKIVE.Tool._cache.favFonts = null;

    const checkForPartialFontFamily = function (textareaToCheck) {
      let hasFontFamily = false;

      $(textareaToCheck.dom).find('tspan').each(function () {
        if ($(this).attr('font-family')) {
          hasFontFamily = true;
        }
      });
      return hasFontFamily;
    };

    const selectedItems = canvas.getSelectedElems();
    const isTable = INKIVE.DesignFacade.getType(selectedItems[0]) === 'table';

    if (isTable) {
      changeTableFontFamily(selectedItems[0], fontFamily, fontWeight);
      return;
    }

    const selectedTextElements = getSelectedTextAreas(selectedItems);

    let textarea = null;
    let range;

    for (let i = 0; i < selectedTextElements.length; i++) {
      textarea = new INKIVE.Text.Textarea().setDOM(selectedTextElements[i]);
      if (!textarea) continue;
      range = canvas.runExtensions('selectionRange', {});
      const containsNumber = /\b[0-9]+\b/g;

      if ((fontFamily.indexOf(' ') > 0 || containsNumber.test(fontFamily)) && !fontFamily.match(/^'(.+)'$/)) {
        fontFamily = '\'' + fontFamily.replace(/'/g, '\\\'') + '\'';
      }
      textarea.text.setPartialStyling(range.start, range.end, 'font-family', fontFamily);

      const selectionBlock = document.querySelector('#text_selectblock path');
      if (checkForPartialFontFamily(textarea) && range.start === range.end && range.start === 0 && !selectionBlock) {
        const styles = [];
        textarea.text.styles.forEach((style) => {
          if (style.attribute !== 'font-family') {
            styles.push(style);
          }
        });
        textarea.text.styles = styles;
        applyStyle();
      } else {
        applyStyle();
      }
    }

    function applyStyle () {
      if (fontStyle) {
        textarea.text.setPartialStyling(range.start, range.end, 'font-style', fontStyle);
      }

      if (!fontWeight && !isNaN(textarea.text.font_weight)) fontWeight = 'normal';
      textarea.text.setPartialStyling(range.start, range.end, 'font-weight', fontWeight);

      if (fontStretch) {
        textarea.text.setPartialStyling(range.start, range.end, 'font-stretch', fontStretch);
      }

      canvas.runExtensions('renderTextArea', { textArea: textarea });
      INKIVE.EditorFacade.once('fonts.added', () => {
        const timer = setInterval(retryRenderText, 1000);
        let retries = 0;

        function retryRenderText () {
          retries += 1;
          if (document.fonts.check(`10px ${textarea.text.font_family}`)) {
            INKIVE.Text.Helpers.memoizedCalculateTextWidth?.cache?.clear();
            canvas.runExtensions('renderTextArea', { textArea: textarea });
            clearInterval(timer);
          } else if (retries >= 5) {
            clearInterval(timer);
          }
        }
      });

      INKIVE.DesignFacade.trigger('text.update');
      popupMenuCommonFunctions.updateFontRelatedMenu(popup);
    }
  });

  function changeTableFontFamily (tableElement, fontFamily, fontWeight) {
    const selectedTexts = getSelectedTextFromTable(tableElement);

    for (const text of selectedTexts) {
      const textarea = new INKIVE.Text.Textarea().setDOM(text);
      textarea.text.checkGlobalPartialStyles();
      textarea.text.styles = textarea.text.styles.filter(style => style.attribute !== 'font-family' && style.attribute !== 'font-weight');
      textarea.text.font_family = fontFamily;
      textarea.text.font_weight = fontWeight;

      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }

    INKIVE.DesignFacade.trigger('table.update');
    svgedit.select.getSelectorManager().requestSelector(tableElement).resize();
  }

  popup.button('on_fontsize').on('change', function (e, value) {
    if (!value) return;
    const selectedItems = canvas.getSelectedElems();
    let sticker;

    for (let i = 0; i < selectedItems.length; i++) {
      const elem = selectedItems[i];
      const isTable = INKIVE.DesignFacade.getType(elem) === 'table';

      if (isTable) {
        value = Math.precision(parseFloat(value), 1);
        changeTableFontSize(elem, value);
        $(this).find('input').val(value);
        return;
      }

      sticker = $(elem).closest('g.element.sticker')[0];

      sticker || changeFontSize(elem);
      sticker && confirmStickerExplode(sticker, function () {
        explodeSticker();
        changeFontSize(elem);
      });
    }
    $(this).find('input').val(value);

    function changeFontSize (element) {
      value = Math.precision(parseFloat(value), 1);

      const textarea = new INKIVE.Text.Textarea().setDOM(element);
      const range = canvas.runExtensions('selectionRange', {});

      const selectionBlock = document.querySelector('#text_selectblock path');
      if (range.start === range.end && !selectionBlock) {
        textarea.text.checkGlobalPartialStyles();
        textarea.text.styles = textarea.text.styles.filter(style => style.attribute !== 'font-size');
        textarea.text.font_size = value;
      } else {
        textarea.text.setPartialStyling(range.start, range.end, 'font-size', value);
      }
      canvas.runExtensions('renderTextArea', { textArea: textarea });
      INKIVE.DesignFacade.trigger('text.update');
    }

    function explodeSticker () {
      INKIVE.DesignFacade.handle(sticker.getAttribute('id'), 'explode');
    }

    function confirmStickerExplode (elem, explode) {
      const message = INKIVE.TranslateHelper.translate($(elem).find('.textarea.subelement')
        ? 'modal.alert.explode_banner'
        : 'modal.alert.explode_placeholder');

      INKIVE.Modal.alertMessage(message, {
        width: 450,
        callback: [explode, $.noop]
      });
    }
  });

  function changeTableFontSize (tableElement, val) {
    const selectedTexts = getSelectedTextFromTable(tableElement);

    for (const text of selectedTexts) {
      const textarea = new INKIVE.Text.Textarea().setDOM(text);
      textarea.text.checkGlobalPartialStyles();
      textarea.text.styles = textarea.text.styles.filter(style => style.attribute !== 'font-size');
      textarea.text.font_size = val;

      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }

    INKIVE.DesignFacade.trigger('table.update');
    svgedit.select.getSelectorManager().requestSelector(tableElement).resize();
  }

  INKIVE.DesignFacade.on('text.update.heading', function (elem) {
    popupMenuCommonFunctions.updateFontRelatedMenu(popup);
  });

  popup.button('on_heading').on('change', function (e, val, fontFamily, fontWeight, heading) {
    if (!heading) return;
    const selectedItems = canvas.getSelectedElems();

    for (let i = 0; i < selectedItems.length; i++) {
      const elem = selectedItems[i];
      changeHeading(elem);
    }

    function changeHeading (element) {
      const textarea = new INKIVE.Text.Textarea().setDOM(element);
      const range = canvas.runExtensions('selectionRange', {});
      let isPartialHeading = false;

      if (range.start === range.end && range.start === 0) {
        INKIVE.DesignFacade.set(element.id, { heading });
      } else {
        const fakeCharsIndices = [0].concat(textarea.text.fakeCharsIndices);
        const { startIndex, endIndex } = calculateStylingIndex(range, fakeCharsIndices);

        textarea.setValues({ heading: null });
        textarea.text.setLineStyling(range.start, range.end, 'heading', heading);
        textarea.text.setPartialStyling(startIndex, endIndex, 'font-size', calculateFontSize(heading));
        textarea.text.setPartialStyling(startIndex, endIndex, 'font-weight', calculateFontWeight(heading));
        canvas.runExtensions('renderTextArea', { textArea: textarea });
        isPartialHeading = true;
      }

      popupMenuCommonFunctions.updateFontRelatedMenu(popup);
      INKIVE.DesignFacade.trigger('text.update.heading', isPartialHeading ? textarea : null);
    }

    function calculateFontSize (heading) {
      const baseSize = Math.min(canvas.contentW, canvas.contentH);
      const fontSize = Math.round(baseSize * config.tableOfContents.defaultHeadingStyle[heading].size);

      return fontSize;
    }

    function calculateFontWeight (heading) {
      return config.tableOfContents.defaultHeadingStyle[heading].fontWeight;
    }

    function calculateStylingIndex (range, fakeCharsIndices) {
      let startIndex = fakeCharsIndices[0];
      let endIndex = fakeCharsIndices[fakeCharsIndices.length - 1];
      let foundStart = false;
      let foundEnd = false;

      for (let i = 0; i < fakeCharsIndices.length; i++) {
        if (!foundEnd && fakeCharsIndices[i] >= range.end) {
          endIndex = fakeCharsIndices[i];
          foundEnd = true;
        }

        if (!foundStart && i > 0 && fakeCharsIndices[i] > range.start && fakeCharsIndices[i - 1] <= range.start) {
          startIndex = fakeCharsIndices[i - 1];
          foundStart = true;
        }

        if (foundEnd && foundStart) break;
      }

      return { startIndex, endIndex };
    }
  });

  popup.button('on_finishTextEditing').on('click', function () {
    canvas.runExtensions('endEditing', {});
  });

  popup.button('on_textEditing').on('click', function () {
    canvas.runExtensions('beginTextEditing', {
      elem: popup.element()
    });

    // TEMP messuring
    !INKIVE.C.IS_DEV && !INKIVE.C.IS_MOBILE && typeof ga === 'function' && ga('send', {
      hitType: 'event',
      eventCategory: 'Editor UI | Popupmenu | text',
      eventAction: 'editing'
    });
  });

  popup.button('on_textTyping').on('click', function () {
    canvas.runExtensions('beginTextTyping', {
      elem: popup.element()
    });

    // TEMP messuring
    !INKIVE.C.IS_DEV && !INKIVE.C.IS_MOBILE && typeof ga === 'function' && ga('send', {
      hitType: 'event',
      eventCategory: 'Editor UI | Popupmenu | text',
      eventAction: 'typing'
    });
  });

  // --- For Fonts -----------------------------------------------------------------------------------------------
  popup.button('on_set-align-justify_start').on('click', function () {
    handleElementAlignment('justify_start', 'justify_start');
  });

  popup.button('on_set-align-justify_middle').on('click', function () {
    handleElementAlignment('justify_middle', 'justify_middle');
  });

  popup.button('on_set-align-justify_end').on('click', function () {
    handleElementAlignment('justify_end', 'justify_end');
  });

  popup.button('on_set-align-justify_all').on('click', function () {
    handleElementAlignment('justify_all', 'justify_all');
  });

  popup.button('on_set-align-left').on('click', function () {
    handleElementAlignment('start', 'left');
  });

  popup.button('on_set-align-center').on('click', function () {
    handleElementAlignment('middle', 'center');
  });

  popup.button('on_set-align-right').on('click', function () {
    handleElementAlignment('end', 'right');
  });

  function handleElementAlignment (textAnchor, alignment) {
    if (INKIVE.DesignFacade.canvas.getMode() === 'textedit') {
      handleTextEditMode(textAnchor, alignment);
    } else {
      handleSelectionMode(textAnchor, alignment);
    }
  }

  function handleTextEditMode (textAnchor, alignment) {
    const textarea = new INKIVE.Text.Textarea().setDOM(popup.element(), false);
    const range = canvas.runExtensions('selectionRange', {});
    textarea.text.setLineStyling(range.start, range.end, 'text-anchor', textAnchor);

    highlightAppliedAlignment(alignment);

    if (alignment.includes('justify')) alignment = alignment.replace('_', '-');
    textAnchor !== 'start' && popup.button('on_set-bullets').removeClass('applied');
    popup.button('on_text-align')[0].querySelector('use').setAttribute('xlink:href', `#text---align-${alignment}`);

    canvas.runExtensions('renderTextArea', { textArea: textarea });
    INKIVE.DesignFacade.trigger('text.update');
  }

  function handleSelectionMode (textAnchor, alignment) {
    INKIVE.DesignFacade.set({ anchor: textAnchor });
    highlightAppliedAlignment(alignment);

    if (alignment.includes('justify')) alignment = alignment.replace('_', '-');
    textAnchor !== 'start' && popup.button('on_set-bullets').removeClass('applied');

    const textElement = INKIVE.DesignFacade.getType(popup.element()) === 'table'
      ? popup.element().querySelector('g.textarea')
      : popup.element();
    const textAttr = new INKIVE.Text.Textarea().setDOM(textElement);
    const iconName = getIconName(textAttr.direction, alignment);

    // Update the icon
    popup.button('on_text-align')[0].querySelector('use').setAttribute('xlink:href', `#text---align-${iconName}`);
    INKIVE.DesignFacade.trigger('text.update');
  }

  function getIconName (textDirection, alignment) {
    const leftToRightFlags = {
      right: 'right',
      left: 'left'
    };

    const rightToLeftFlags = {
      right: 'left',
      left: 'right'
    };

    let iconName = textDirection === 'rtl'
      ? rightToLeftFlags[alignment]
      : leftToRightFlags[alignment];

    iconName = iconName || alignment;
    return iconName;
  }

  function highlightAppliedAlignment (alignment) {
    _.each(['right', 'justify_start', 'justify_middle', 'justify_end', 'justify_all', 'center', 'left'], function (a) {
      popup.button('on_set-align-' + a).toggleClass('applied', a === alignment);
    });
  }

  popup.button('on_set-paragraph-spacing')
    .on('start', function () {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
    })
    .on('change', _.throttle(function (e, val) {
      if (typeof val === 'undefined') return;
      textarea.text.setValues({ paragraph_spacing: val });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }, shortTimeOut))
    .on('stop', function (e, val) {
      INKIVE.DesignFacade.set({ spacing: { paragraph: parseFloat(val) } });
    });

  popup.button('on_set-first-line-margin')
    .on('start', function () {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
      calculateTablePaddingLeft();
    })
    .on('change', _.throttle(function (e, val) {
      if (typeof val === 'undefined') return;
      textarea.text.setValues({ first_line_margin: parseFloat(val) + tablePaddingLeft });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }, shortTimeOut))
    .on('stop', function (e, val) {
      INKIVE.DesignFacade.set({ spacing: { first_line_margin: parseFloat(val) + tablePaddingLeft } });
    });

  popup.button('on_set-right-margin')
    .on('start', function () {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
      calculateTablePaddingLeft();
    })
    .on('change', _.throttle(function (e, val) {
      if (typeof val === 'undefined') return;
      textarea.text.setValues({ right_margin: val + tablePaddingRight });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }, shortTimeOut))
    .on('stop', function (e, val) {
      INKIVE.DesignFacade.set({ spacing: { right_margin: parseFloat(val) + tablePaddingRight } });
    });

  popup.button('on_set-line-spacing')
    .on('start', function () {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
    })
    .on('change', _.throttle(function (e, val) {
      if (typeof val === 'undefined') return;
      textarea.text.setValues({ line_spacing: parseFloat(val) });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }, shortTimeOut))
    .on('stop', function (e, val) {
      INKIVE.DesignFacade.set({ spacing: { line: parseFloat(val) } });
    });

  popup.button('on_set-word-spacing')
    .on('start', function () {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element(), false);
    })
    .on('change', _.throttle(function (e, val) {
      if (typeof val === 'undefined') return;
      textarea.text.setValues({ word_spacing: parseFloat(val) });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }, shortTimeOut))
    .on('stop', function (e, val) {
      if (typeof val === 'undefined') return;
      textarea.text.setValues({ word_spacing: parseFloat(val) });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
      INKIVE.DesignFacade.trigger('text.update');
    });

  popup.button('on_set-char-spacing')
    .on('start', function () {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element(), false);
    })
    .on('change', _.throttle(function (e, val) {
      if (typeof val === 'undefined') return;
      textarea.text.setValues({ letter_spacing: INKIVE.Text.Helpers.convertLetterSpacing(val, textarea.text.font_size) });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }, shortTimeOut))
    .on('stop', function (e, val) {
      if (typeof val === 'undefined') return;
      textarea.text.setValues({ letter_spacing: INKIVE.Text.Helpers.convertLetterSpacing(val, textarea.text.font_size) });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
      INKIVE.DesignFacade.trigger('text.update');
    });

  function setParagraphLetterSpacing (val) {
    const selectionContent = textarea.text?.textContent?.slice(range.start, range.end) || '';
    const positionToShift = selectionContent.length - TextStylingService.getRealContentLength(selectionContent);

    const newLetterSpacing = Array.isArray(textarea.text.letter_spacing)
      ? textarea.text.letter_spacing
      : new Array(textarea.text.textContent.length).fill(textarea.text.letter_spacing || 0);

    for (let i = range.start; i <= range.end - positionToShift; i++) {
      newLetterSpacing[i] = convertRemToPixel(parseFloat(val), textarea.text.font_size);
    }

    textarea.text.setValues({ letter_spacing: newLetterSpacing });
    canvas.runExtensions('renderTextArea', { textArea: textarea });
  }

  popup.button('on_set-paragraph-char-spacing')
    .on('start', function () {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element(), false);
      range = canvas.runExtensions('selectionRange', {});

      const contentBeforeCursorPosition = textarea.text.textContent.slice(0, range.start);
      const positionToShift = contentBeforeCursorPosition.length - TextStylingService.getRealContentLength(contentBeforeCursorPosition);

      range.start = range.start - positionToShift;
      range.end = range.end - positionToShift;
    })
    .on('change', _.throttle(function (e, val) {
      if (typeof val === 'undefined') return;
      setParagraphLetterSpacing(val);
    }, shortTimeOut))
    .on('stop', function (e, val) {
      if (typeof val === 'undefined') return;
      setParagraphLetterSpacing(val);
      INKIVE.DesignFacade.trigger('text.update');
    });

  popup.button('on_set-line-margin')
    .on('start', function (e, val) {
      calculateTablePaddingLeft();
      tempMargin = parseFloat(val) + tablePaddingLeft;
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
      numberOfParagraphs = INKIVE.Text.Helpers.getParagraphCount(textarea);
      textAnchorFactor = calculateMainTextAnchorFactor();
      firstLineMargin = Array.isArray(textarea.text.first_line_margin)
        ? textarea.text.first_line_margin
        : Array.from({ length: numberOfParagraphs }).fill(textarea.text.first_line_margin || 0);
    })
    .on('change', _.throttle(function (e, val) {
      updateLineMargin(val);
    }, shortTimeOut))
    .on('stop', function (e, val) {
      updateLineMargin(val);
    });

  function calculateMainTextAnchorFactor () {
    const result = [];
    const reverseFactor = textarea.text.direction === 'rtl' ? -1 : 1;
    for (let i = 0; i < textarea.text.strings.length; i++) {
      const string = textarea.text.strings[i];
      if (i === 0 || (i > 0 && textarea.text.strings[i - 1].isEol)) {
        const mainTextAnchor = string.textAnchor || textarea.text.text_anchor;
        const factor = mainTextAnchor.includes('end') ? -1 : 1;
        result.push(factor * reverseFactor);
      }
    }

    return result;
  }

  function calculateTablePaddingLeft () {
    const tableCell = popup.element().parentElement;

    if (tableCell?.classList?.contains('table-cell')) {
      const tablePadding = tableQuery.getCellPadding(tableCell);
      tablePaddingLeft = tablePadding.left;
      tablePaddingRight = tablePadding.right;
      isTableCell = true;
    } else {
      tablePaddingLeft = 0;
      tablePaddingRight = 0;
      isTableCell = false;
    }
  }

  function updateLineMargin (val) {
    if (typeof val === 'undefined') return;
    val = parseFloat(val) + tablePaddingLeft;

    if (isTableCell) {
      textarea.text.setValues({ line_margin: val });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
      return;
    }

    for (let i = 0; i < firstLineMargin.length; i++) {
      if (textAnchorFactor[i] === -1) continue;
      if (firstLineMargin[i] === 0 && tempMargin !== 0) firstLineMargin[i] = val;
      const newFirstLineMargin = getNewFirstLineMargin(firstLineMargin[i] + (val - tempMargin));
      firstLineMargin[i] = newFirstLineMargin > 0 ? newFirstLineMargin : 1;
      if (newFirstLineMargin <= 0 && val === 0) firstLineMargin[i] = 0;
    }
    updateSliderValue(firstLineMargin[0] - tablePaddingLeft, 'firstLineMargin');
    tempMargin = val;
    textarea.text.setValues({
      line_margin: val,
      first_line_margin: firstLineMargin
    });
    canvas.runExtensions('renderTextArea', { textArea: textarea });
  }

  function getNewFirstLineMargin (firstLineMargin) {
    return firstLineMargin < maxLineMargin ? firstLineMargin : maxLineMargin;
  }

  function getParagraphUpdatedSpacing (textarea, spacingName, defaultValue, value, paragraphIndexes) {
    let spacing = textarea.text[spacingName];
    if (!Array.isArray(spacing)) {
      spacing = Array.isArray(defaultValue)
        ? defaultValue
        : Array.from({ length: INKIVE.Text.Helpers.getParagraphCount(textarea) }).fill(defaultValue);
    }
    paragraphIndexes.forEach(index => { spacing[index] = value; });
    return spacing;
  }

  popup.button('on_set-paragraph-spacing-dy')
    .on('start', function () {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
      range = canvas.runExtensions('selectionRange', {});
      activeParagraphIndex = INKIVE.Text.Helpers.getParagraphIndexesFromCursorSelection(textarea, range);
    })
    .on('change', _.throttle(function (e, val) {
      if (typeof val === 'undefined') return;
      const paragraphSpacing = getParagraphUpdatedSpacing(textarea, 'paragraph_spacing', textarea.text.paragraph_spacing, val, activeParagraphIndex);
      textarea.text.setValues({ paragraph_spacing: paragraphSpacing });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }, shortTimeOut))
    .on('stop', function (e, val) {
      const paragraphSpacing = getParagraphUpdatedSpacing(textarea, 'paragraph_spacing', textarea.text.paragraph_spacing, val, activeParagraphIndex);
      INKIVE.DesignFacade.set({ spacing: { paragraph: paragraphSpacing } });
    });

  popup.button('on_set-paragraph-first-line-margin')
    .on('start', function () {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
      range = canvas.runExtensions('selectionRange', {});
      activeParagraphIndex = INKIVE.Text.Helpers.getParagraphIndexesFromCursorSelection(textarea, range);
      calculateTablePaddingLeft();
    })
    .on('change', _.throttle(function (e, val) {
      if (typeof val === 'undefined') return;
      const firstLineMargin = getParagraphUpdatedSpacing(textarea, 'first_line_margin', textarea.text.first_line_margin, val + tablePaddingLeft, activeParagraphIndex);
      textarea.text.setValues({ first_line_margin: firstLineMargin });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }, shortTimeOut))
    .on('stop', function (e, val) {
      const firstLineMargin = getParagraphUpdatedSpacing(textarea, 'first_line_margin', textarea.text.first_line_margin, val + tablePaddingLeft, activeParagraphIndex);
      INKIVE.DesignFacade.set({ spacing: { first_line_margin: firstLineMargin } });
    });

  popup.button('on_set-paragraph-right-margin')
    .on('start', function () {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
      range = canvas.runExtensions('selectionRange', {});
      activeParagraphIndex = INKIVE.Text.Helpers.getParagraphIndexesFromCursorSelection(textarea, range);
      calculateTablePaddingLeft();
    })
    .on('change', _.throttle(function (e, val) {
      if (typeof val === 'undefined') return;
      const rightMargin = getParagraphUpdatedSpacing(textarea, 'right_margin', textarea.text.right_margin, val, activeParagraphIndex);
      textarea.text.setValues({ right_margin: rightMargin });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }, shortTimeOut))
    .on('stop', function (e, val) {
      const rightMargin = getParagraphUpdatedSpacing(textarea, 'right_margin', textarea.text.right_margin, val, activeParagraphIndex);
      INKIVE.DesignFacade.set({ spacing: { right_margin: rightMargin } });
    });

  popup.button('on_set-paragraph-margin')
    .on('start', function (e, val) {
      calculateTablePaddingLeft();
      tempMargin = parseFloat(val) + tablePaddingLeft;
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
      range = canvas.runExtensions('selectionRange', {});
      activeParagraphIndex = INKIVE.Text.Helpers.getParagraphIndexesFromCursorSelection(textarea, range);
      textAnchorFactor = calculateMainTextAnchorFactor();
      firstLineMargin = Array.isArray(textarea.text.first_line_margin)
        ? textarea.text.first_line_margin
        : Array.from({ length: INKIVE.Text.Helpers.getParagraphCount(textarea) }).fill(textarea.text.first_line_margin || 0);
    })
    .on('change', _.throttle(function (e, val) {
      updateParagraphMargin(val);
    }, shortTimeOut))
    .on('stop', function (e, val) {
      updateParagraphMargin(val);
    });

  function updateParagraphMargin (val) {
    if (typeof val === 'undefined') return;
    val = parseFloat(val) + tablePaddingLeft;

    const lineMargin = getParagraphUpdatedSpacing(textarea, 'line_margin', textarea.text.line_margin, val, activeParagraphIndex);

    if (isTableCell) {
      textarea.text.setValues({ line_margin: lineMargin });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
      return;
    }

    for (let i = 0; i < lineMargin.length; i++) {
      if (!activeParagraphIndex.includes(i)) continue;
      if (textAnchorFactor[i] === -1) continue;
      if (firstLineMargin[i] === 0 && tempMargin !== 0) firstLineMargin[i] = tempMargin;
      const newFirstLineMargin = getNewFirstLineMargin(firstLineMargin[i] + (val - tempMargin));
      firstLineMargin[i] = newFirstLineMargin > 0 ? newFirstLineMargin : 1;
      if (newFirstLineMargin <= 0 && val === 0) firstLineMargin[i] = 0;
    }
    tempMargin = val;
    updateSliderValue(firstLineMargin[activeParagraphIndex[0]] - tablePaddingLeft, 'firstLineMargin');
    textarea.text.setValues({
      line_margin: lineMargin,
      first_line_margin: firstLineMargin
    });
    canvas.runExtensions('renderTextArea', { textArea: textarea });
  }

  function updateColumnSlider (columns, gap, textWidth) {
    const columnWidthInput = document.querySelector('input#columnWidth');
    const columnGapInput = document.querySelector('input#columnGap');
    const gapToSet = gap || config.text.columns.defaultGap;
    const minGap = config.text.columns.minGap;
    const maxGap = config.text.columns.maxGap;
    const columnWidthToSet = calculateColumnsWidth(textWidth, columns, gap);
    const minColumnsWidth = calculateColumnsWidth(textWidth, columns, maxGap);
    const maxColumnsWidth = calculateColumnsWidth(textWidth, columns, minGap);

    if (columnWidthInput) {
      const slider = $(columnWidthInput.parentNode).find('.slider');
      slider && slider.slider('option', 'min', minColumnsWidth);
      slider && slider.slider('option', 'max', maxColumnsWidth);
      updateSliderValue(columnWidthToSet, 'columnWidth');
    }

    if (columnGapInput) {
      const slider = $(columnGapInput.parentNode).find('.slider');
      slider && slider.slider('option', 'min', minGap);
      slider && slider.slider('option', 'max', maxGap);
      updateSliderValue(gapToSet, 'columnGap');
    }
  }

  popup.button('on_set-paragraph-dy')
    .on('start', function () {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
      range = canvas.runExtensions('selectionRange', {});
      activeParagraphIndex = INKIVE.Text.Helpers.getParagraphIndexesFromCursorSelection(textarea, range);
    })
    .on('change', _.throttle(function (e, val) {
      if (typeof val === 'undefined') return;
      const lineSpacing = getParagraphUpdatedSpacing(textarea, 'line_spacing', textarea.text.line_spacing, val, activeParagraphIndex);
      textarea.text.setValues({ line_spacing: lineSpacing });
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }, shortTimeOut))
    .on('stop', function (e, val) {
      const lineSpacing = getParagraphUpdatedSpacing(textarea, 'line_spacing', textarea.text.line_spacing, val, activeParagraphIndex);
      INKIVE.DesignFacade.set({ spacing: { line: lineSpacing } });
    });

  function setColumnNumber (value) {
    if (typeof value === 'undefined') return;
    updateColumnSlider(parseInt(value), textarea.text.columns_gap, textarea.width);
    INKIVE.DesignFacade.set({ columns: parseInt(value) });
  }

  popup.button('on_set-column-number')
    .on('start', function (e, val) {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
    })
    .on('change', _.throttle(function (e, val) {
      setColumnNumber(val);
    }, shortTimeOut))
    .on('stop', function (e, val) {
      setColumnNumber(val);
    });

  function setColumnGap (value) {
    if (typeof value === 'undefined') return;
    const columnsGap = parseInt(value);
    const columnsWidth = parseInt((textarea.width - columnsGap * (columns - 1)) / columns);
    updateSliderValue(columnsWidth, 'columnWidth');
    INKIVE.DesignFacade.set({ columns_gap: parseInt(value) });
  }

  popup.button('on_set-column-gap')
    .on('start', function (e, val) {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
      columns = Math.max(textarea.text.columns, 2);
    })
    .on('change', _.throttle(function (e, val) {
      setColumnGap(val);
    }, shortTimeOut))
    .on('stop', function (e, val) {
      setColumnGap(val);
    });

  function setColumnWidth (value) {
    if (typeof value === 'undefined') return;
    const columnsWidth = parseInt(value);
    const columnsGap = parseInt((textarea.width - columnsWidth * columns) / (columns - 1));
    updateSliderValue(columnsGap, 'columnGap');
    INKIVE.DesignFacade.set({ columns_gap: parseInt(columnsGap) });
  }

  popup.button('on_set-column-width')
    .on('start', function (e, val) {
      textarea = new INKIVE.Text.Textarea().setDOM(popup.element());
      columns = Math.max(textarea.text.columns, 2);
    })
    .on('change', _.throttle(function (e, val) {
      setColumnWidth(val);
    }, shortTimeOut))
    .on('stop', function (e, val) {
      setColumnWidth(val);
    });

  popup.button('on_set-auto-adjustable').on('click', function (e) {
    e.preventDefault();
    INKIVE.DesignFacade.set({
      auto_adjustable: !$(this).hasClass('applied')
    });

    popupMenuCommonFunctions.toggleTextSpacingForJustification(popup,
      popup.button('on_set-align-justify').is(':not(.applied)'));
  });

  INKIVE.DesignFacade
    .off('text.update.bullet_points')
    .on('text.update.bullet_points', onBulletPointsUpdate)
    .off('text.update.ordered_list')
    .on('text.update.ordered_list', onOrderedListUpdate)
    .off('text.update.auto_adjustable')
    .on('text.update.auto_adjustable', onAutoAdjustableUpdate)
    .off('text.update.anchor')
    .on('text.update.anchor', onAnchorUpdate);

  function onBulletPointsUpdate (bulletPoints) {
    popup.button('on_set-bullets').toggleClass('applied', !!Number(bulletPoints) === true);
    if (!Number(bulletPoints)) return;
    popup.button('on_set-list').toggleClass('applied', !!Number(bulletPoints) === false);
  }

  function onOrderedListUpdate (orderedList) {
    popup.button('on_set-list').toggleClass('applied', !!Number(orderedList) === true);
    if (!Number(orderedList)) return;
    popup.button('on_set-bullets').toggleClass('applied', !!Number(orderedList) === false);
  }

  function onAutoAdjustableUpdate (autoAdjustable) {
    popup.button('on_set-auto-adjustable').toggleClass('applied', autoAdjustable === true);
  }

  function onAnchorUpdate (anchor) {
    popup.button('on_set-align-left').toggleClass('applied', anchor === 'start');
    popup.button('on_set-align-right').toggleClass('applied', anchor === 'end');
    popup.button('on_set-align-center').toggleClass('applied', anchor === 'middle');
    popup.button('on_set-align-justify_start').toggleClass('applied', anchor === 'justify_start');
    popup.button('on_set-align-justify_middle').toggleClass('applied', anchor === 'justify_middle');
    popup.button('on_set-align-justify_end').toggleClass('applied', anchor === 'justify_end');
    popup.button('on_set-align-justify_all').toggleClass('applied', anchor === 'justify_all');

    popupMenuCommonFunctions.toggleTextSpacingForJustification(popup, !anchor.includes('justify'));
  }

  popup.button('on_set-bold').on('click', _.partial(onTextStyle, function (textarea, range, value) {
    textarea.text.setPartialStyling(range.start, range.end, 'font-weight', value ? 'bold' : 'normal');
  }));

  popup.button('on_set-italic').on('click', _.partial(onTextStyle, function (textarea, range, value) {
    textarea.text.setPartialStyling(
      range.start, range.end, 'font-style',
      value
        ? 'italic'
        : 'normal'
    );
  }));

  popup.button('on_set-underline').on('click', _.partial(onTextStyle, function (textarea, range, value) {
    textarea.text.setPartialStyling(
      range.start, range.end, 'text-decoration',
      value
        ? 'underline'
        : 'normal'
    );
  }));

  popup.button('on_convert-dx').on('click', function () {
    INKIVE.Text.Helpers.convertDxToSpace(popup.element());
    INKIVE.DesignFacade.trigger('text.update');
  });

  popup.button('on_removePartialStyle').on('click', _.partial(onTextStyle, function (textarea, range) {
    textarea.text.removePartialStyling(range.start, range.end);
  }));

  function onTextStyle (doChange, event) {
    const isRemoveStyle = $(this).attr('data-action') === 'on_removePartialStyle';
    if ($(this).parents('li.semidisabled').length) return;
    event.preventDefault();

    const selectedItems = canvas.getSelectedElems();
    for (let i = 0; i < selectedItems.length; i++) {
      const elem = selectedItems[i];
      const textarea = new INKIVE.Text.Textarea().setDOM(elem);
      if (!textarea) continue;
      const range = canvas.runExtensions('selectionRange', {});
      doChange.call(this, textarea, range, !$(this).is('.applied'));
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }

    if (isRemoveStyle) {
      $(this).closest('ul').children().each(function (index, elem) {
        $(elem).children().removeClass('applied');
      });
    } else {
      $(this).toggleClass('applied');
    }
    popupMenuCommonFunctions.updateFontRelatedMenu(popup);
  }

  popup.button('on_set-ucase').on('click', _.partial(toggleTextCase, true));
  popup.button('on_set-lcase').on('click', _.partial(toggleTextCase, false));

  function toggleTextCase (value, event) {
    event.preventDefault();

    const selectedItems = canvas.getSelectedElems();
    for (let i = 0; i < selectedItems.length; i++) {
      const elem = selectedItems[i];
      const textarea = new INKIVE.Text.Textarea().setDOM(elem);
      const range = canvas.runExtensions('selectionRange', {});
      textarea.text.setTextCase(range.start, range.end, value);
      canvas.runExtensions('renderTextArea', { textArea: textarea });
    }

    INKIVE.DesignFacade.trigger('text.update');
  }
}

export function updateSliderValue (flagValue, flagId) {
  const button = document.querySelector(`input#${flagId}`);
  if (button && !isNaN(flagValue)) {
    button.value = flagValue;
    const slider = $(button.parentNode).find('.slider');

    if (!slider) return;

    const min = slider.slider('option', 'min');
    const max = slider.slider('option', 'max');
    slider.slider('value', flagValue);
    slider.css('width', `${(flagValue - min) / (max - min) * 100}%`);
  }
}
